import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

// Sober DS uses custom `text-so-{11,12,13,14,16,20,28}` font-size tokens. Default
// tailwind-merge classifies any `text-*` as the same group, so pairing a size
// (text-so-14) with a color (text-sober-text) collapses to one — silently
// dropping the font-size. Register `so-*` as the font-size scale so size and
// color stay in distinct conflict groups.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['so-11', 'so-12', 'so-13', 'so-14', 'so-16', 'so-20', 'so-28'] }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format currency in BRL
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100); // Assuming value is in cents
}

// Format phone number for display — supports Brazilian and international formats
// Hotfix phone opcional (RC-039): aceita NULL — paciente pode não ter telefone.
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  // Remove non-digits except leading +
  const hasPlus = phone.startsWith('+');
  const digits = phone.replace(/\D/g, '');

  if (digits.length === 13 && digits.startsWith('55')) {
    // BR with DDI: +55 11 99999-9999
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  } else if (digits.length === 12 && digits.startsWith('55')) {
    // BR landline with DDI: +55 11 9999-9999
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  } else if (digits.length === 11 && !hasPlus) {
    // BR mobile without DDI: (11) 99999-9999
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10 && !hasPlus) {
    // BR landline without DDI: (11) 9999-9999
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  } else if (digits.length > 8) {
    // Generic international: always prefix with + (any >8-digit number that
    // isn't a plain BR 10/11-digit must carry a country code)
    return `+${digits}`;
  }

  return phone;
}

/**
 * Converts a stored phone number (digits-only or with DDI) to E.164 format
 * for use with the PhoneInput component.
 * - "11999887766"      → "+5511999887766"  (BR mobile, assume +55)
 * - "5511999887766"    → "+5511999887766"  (already has BR DDI)
 * - "+5511999887766"   → "+5511999887766"  (already E.164)
 * - "12125551234"      → "+12125551234"    (US number with DDI)
 */
export function toE164(phone: string | null | undefined): string {
  if (!phone) return '';
  if (phone.startsWith('+')) return phone;
  const digits = phone.replace(/\D/g, '');
  // 13 digits starting with 55 → BR with DDI
  if ((digits.length === 13 || digits.length === 12) && digits.startsWith('55')) {
    return `+${digits}`;
  }
  // 10-11 digits without DDI → assume BR
  if (digits.length === 10 || digits.length === 11) {
    return `+55${digits}`;
  }
  // Otherwise prefix with +
  return `+${digits}`;
}

// Format date relative to now
export function formatRelativeDate(date: string | Date): string {
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes}min atrás`;
  if (hours < 24) return `${hours}h atrás`;
  if (days < 7) return `${days}d atrás`;

  return d.toLocaleDateString('pt-BR');
}

// CPF mask for input
export function cpfMask(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

// Phone mask for input
export function phoneMask(value: string): string {
  const digits = value.replace(/\D/g, '');

  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}
